import React, { useCallback, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import Button from '@material-ui/core/Button';
import { taskDocumentType, onboardingTasks } from '@ourbranch/lookups';
import { IconButton, LinearProgress, Menu, MenuItem } from '@material-ui/core';
import { Label } from 'core/components/label';
import { Tag } from 'core/components/tag';
import { capitalize, dateTimeFormatter } from 'core/helpers/formatters';
import { IconButtonWithTooltip } from 'core/components/icon-button-with-tooltip';
import { useToast } from 'core/components/toast';
import { useStore } from 'core/store';
import useSession from 'core/hooks/use-session';
import useStorage from 'customer/hooks/use-storage';
import arrowIcon from 'core/assets/svg/up-right-arrow.svg';
import viewIcon from 'customer/assets/view.svg';
import undoIcon from 'customer/assets/undo.svg';
import uploadIcon from 'customer/assets/upload.svg';
import completedIcon from 'customer/assets/completed.svg';
import ResetModal from '../reset-task/reset-task-modal';
import useStyles from './task.styles';

const getDocName = (taskName, policyData) => {
  const { policyType, policyId } = policyData;
  const now = new Date();
  switch (taskName) {
    case onboardingTasks.SignApplication:
      return `${now.getFullYear()}-${
        now.getMonth() + 1
      }-${now.getDate()}-${now.getHours()}-${now.getMinutes()}-${policyType[0].toUpperCase()}_APPL`;
    case onboardingTasks.CompleteInventory:
      return `inventory_${policyId}`;
    case onboardingTasks.ConnectedHomeRenewal:
      return `connected_home_renewal_${policyId}`;
    case onboardingTasks.ConfirmWindstormMitigationStatus:
      return `proof_of_advanced_windstorm_mitigation_${policyId}`;
    default:
      return null;
  }
};

const getTaskDisplayName = (taskName) => {
  switch (taskName) {
    case onboardingTasks.NeedProofThatAccidentPreventionCourseTaken:
      return 'Proof of accident prevention certification';
    case onboardingTasks.NeedProofOfPriorCoverage:
      return 'Proof of prior auto insurance coverage';
    case onboardingTasks.ConfirmWindstormMitigationStatus:
      return 'Proof of advanced windstorm mitigation';
    case onboardingTasks.SignApplication:
      return 'Sign policy documents';
    case onboardingTasks.ConnectedHomeRenewal:
      return 'Proof of Connected Home for renewal';
    case onboardingTasks.ProvideProofOfConnectedHome:
      return 'Proof of Connected Home';
    default:
      return taskName;
  }
};

const appcuesModalIdsByTaskName = {
  [onboardingTasks.SignApplication]: '63249237-5e04-4437-a496-12ff76cd2648',
  [onboardingTasks.NeedProofOfPriorCoverage]: '5251e741-2a75-4e50-b245-5a1cef831dae',
  [onboardingTasks.ProvideProofOfConnectedHome]: '469fd3b0-415f-4b87-b355-8e9998f6a0ae',
  [onboardingTasks.ConnectedHomeRenewal]: '8417a4bd-f165-48c3-a01c-a845f2ff0029',
  [onboardingTasks.ProvideProofOfGoodStudent]: '3e9f15c2-1e9d-46d7-a0cd-bbbbfb300a0b',
  [onboardingTasks.NeedProofThatAccidentPreventionCourseTaken]: 'b3047d65-9347-4a65-bd77-70deb638bb1b',
  [onboardingTasks.ConfirmWindstormMitigationStatus]: '416d477c-d154-4e60-bd07-ead1f62a4582',
  [onboardingTasks.CompleteInventory]: '473c8d96-e71d-4ea5-83a6-ccf5078c96ea',
  [onboardingTasks.NeedScheduledPersonalPropertyImages]: '9570ff2c-f404-479f-b11c-72373cc01eb5'
};

const tasksThatNeedReview = [
  onboardingTasks.NeedProofOfPriorCoverage,
  onboardingTasks.NeedProofThatAccidentPreventionCourseTaken,
  onboardingTasks.ConfirmWindstormMitigationStatus,
  onboardingTasks.ProvideProofOfConnectedHome,
  onboardingTasks.ConnectedHomeRenewal
];

const Task = ({ taskId, taskName, data, completed, completedDate, policyId, readOnly, completedBy, rejections }) => {
  const classes = useStyles();
  const {
    account: { id, completeTask, resetTask }
  } = useStore();
  const { canResetOnboardingTasks, isAgency } = useSession();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const toast = useToast();
  const { customer, documentType, files, documentUrl: singleDocUrl } = data || {};
  const documentUrl = singleDocUrl ? singleDocUrl : files?.length === 1 ? files?.[0]?.documentUrl : null;
  const { upload, download } = useStorage({
    onUpload: async (path) => {
      setProgress(0);
      setUploading(false);
      const res = await completeTask({
        taskId,
        accountId: id,
        documentUrl: path,
        policyId,
        taskName,
        data,
        submittedByIA: isAgency
      });

      const toastMessage =
        isAgency && tasksThatNeedReview.includes(taskName)
          ? `The ${taskName} task ${customer ? `for ${customer} ` : ''}has been submitted. Branch will review them within 2 to 3 days and notify you if there are issues with the document.`
          : `${taskName} ${customer ? `for ${customer} ` : ''}has been marked complete and uploaded to ${
              documentType ? documentType.toLowerCase() : ''
            } documents.`;

      if (res.ok) {
        toast.notify({
          type: 'success',
          message: toastMessage
        });
      } else {
        toast.notify({
          type: 'error',
          message: `There was an error completing the task - ${res.error}`
        });
      }
    },
    onProgress: (progress) => {
      setProgress((progress.loaded / progress.total) * 100);
    }
  });

  const [showResetModal, setShowResetModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const fileRef = useRef(null);
  const [, type] = policyId.split('-');

  const handleUpload = useCallback(
    async ({ target: { files } }) => {
      setUploading(true);
      const fileName =
        getDocName(taskName, { policyType: type, policyId }) || `${taskName}${customer ? ` - ${customer}` : ''}`;
      let path = '';
      const [file] = files;
      const splitName = file.name.split('.');
      const ext = splitName[splitName.length - 1];

      if (documentType === taskDocumentType.Internal) {
        path = `internal/${id}/${fileName}.${ext}`;
      } else if (fileName) {
        path = `docs/${id}/customer/${policyId}/${fileName}.${ext}`;
      }

      if (path) {
        await upload(path, file);
      }
    },
    [taskName, id, policyId, customer, documentType, type, upload]
  );
  const handleReset = async () => {
    const res = await resetTask({
      accountId: id,
      taskId,
      policyId,
      taskName,
      completedBy,
      rejections
    });
    if (res.ok) {
      toast.notify({
        type: 'success',
        message: `The "${taskName}" task has been reset. `
      });
    } else {
      toast.notify({
        type: 'error',
        message: `There was an error resetting the task - ${res.error}`
      });
    }
  };

  const showResetTaskIcon =
    canResetOnboardingTasks &&
    !(taskName === onboardingTasks.CompleteInventory) &&
    !(completedBy === 'member' && taskName === onboardingTasks.SignApplication);

  const handleViewFile = useCallback(
    (event) => {
      if (documentUrl) {
        download(documentUrl);
      } else if (files?.length > 1) {
        setAnchorEl(event.currentTarget);
      }
    },
    [documentUrl, files, download]
  );

  const handleCloseFileMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const appcuesModalId = appcuesModalIdsByTaskName[taskName];

  return (
    <div className={classNames(classes.container, { [classes.completed]: completed })}>
      <div className={classes.task}>
        {completed && <img alt="rounded checkmark" className={classes.completedIcon} src={completedIcon} />}
        <div>
          <div className={classNames(classes.taskInfo, { [classes.taskInfoCompleted]: completed })}>
            <div className={classes.taskNameContainer}>
              <Label type="infoValue" className={classNames(classes.taskName, { [classes.completedTask]: completed })}>
                {getTaskDisplayName(taskName)}
              </Label>
              {appcuesModalId && (
                <IconButton
                  className={classes.learnMoreLink}
                  onClick={() => {
                    window?.Appcues?.show(appcuesModalId);
                  }}
                >
                  <div className={classes.learnMoreLinkText}>Learn More</div>
                  <img src={arrowIcon} height={14} width={14} alt="Learn More" />
                </IconButton>
              )}
            </div>
            {completedDate && (
              <Label variant="darkGreenSmall" className={classes.completedAt}>
                Submitted on {dateTimeFormatter(completedDate)} EST by {completedBy}
              </Label>
            )}
          </div>
          <div className={classes.tags}>
            <Tag className={classNames({ [classes.autoTag]: type === 'auto' })}>
              {capitalize(type)}
              {customer ? ` - ${customer}` : ''}
            </Tag>
          </div>
        </div>
      </div>
      {completed ? (
        <div className={classes.task}>
          <div className={classes.iconButtons}>
            {showResetTaskIcon && (
              <IconButtonWithTooltip
                onClick={() => setShowResetModal(true)}
                icon={undoIcon}
                tooltip="Reset task"
                alt="undo"
              />
            )}
            {(documentUrl || files?.length) && (
              <IconButtonWithTooltip
                onClick={handleViewFile}
                height={40}
                width={40}
                icon={viewIcon}
                tooltip="View file"
                alt="eye"
                aria-controls="tooltip-menu"
                aria-haspopup="true"
                className={classes.iconButton}
              />
            )}
            {files && files?.length > 1 && (
              <Menu
                id="tooltip-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleCloseFileMenu}
              >
                {files.map((f, index) => {
                  return (
                    <MenuItem key={index} onClick={() => download(f.documentUrl)}>
                      View file {index + 1} of {files.length}
                    </MenuItem>
                  );
                })}
              </Menu>
            )}
          </div>
        </div>
      ) : (
        <div>
          {!readOnly && (
            <>
              <input
                ref={fileRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={handleUpload}
                id={`onboarding-task-${taskName}`}
              />
              <Button
                disabled={uploading}
                onClick={() => {
                  fileRef.current.click();
                }}
                disableFocusRipple
                className={classes.upload}
                variant="text"
                color="secondary"
              >
                Upload
                <img alt="upload icon" className={classNames(classes.uploadIcon)} src={uploadIcon} />
              </Button>
            </>
          )}
        </div>
      )}
      {uploading && (
        <LinearProgress className={classes.progressBar} color="secondary" variant="determinate" value={progress} />
      )}

      <ResetModal
        open={showResetModal}
        onClose={() => setShowResetModal(false)}
        taskName={taskName}
        onReset={handleReset}
        completedBy={completedBy}
        rejections={rejections}
      />
    </div>
  );
};

Task.propTypes = {
  taskId: PropTypes.string.isRequired,
  taskName: PropTypes.string.isRequired,
  data: PropTypes.object,
  completed: PropTypes.bool.isRequired,
  completedDate: PropTypes.string,
  readOnly: PropTypes.bool,
  completedBy: PropTypes.string,
  rejections: PropTypes.array,
  policyId: PropTypes.string
};

Task.defaultProps = {
  data: {},
  completedDate: null,
  readOnly: false
};

export default observer(Task);
